<template>
  <div :class="bem()">
    <Swiper
      :slides-per-view="4.5"
      :space-between="8"
      breakpoints-base="container"
      :class="bem('row')"
      :breakpoints="{
        440: {
          slidesPerView: 5.5,
        },
        580: {
          slidesPerView: 6.5,
        },
        700: {
          slidesPerView: 7.5,
        },
      }"
      @swiper="setSwiperInstance"
    >
      <SwiperSlide
        v-for="(variant, index) in standardHeroDesigns"
        :key="index"
        :class="
          bem('row-item', {
            active: props.activeSwatchIndex === index,
          })
        "
      >
        <button
          :class="bem('row-btn')"
          @click="emits('active-swatch-index-changed', index)"
        >
          <DynamicImg
            v-if="variant.legacySKU"
            :sku="variant.legacySKU"
            :width="160"
            :height="160"
            bg="transparent"
            :view-angle="2"
          />
          <span
            v-if="variant.swatchIcon"
            :class="bem('badge')"
          >
            <img :src="variant.swatchIcon" />
          </span>
        </button>
      </SwiperSlide>
      <template v-if="limitedEditionDesigns?.length">
        <SwiperSlide
          v-for="(variant, index) in limitedEditionDesigns"
          :key="index"
          :class="
            bem('row-item', {
              active:
                props.activeSwatchIndex === standardHeroDesigns.length + index,
            })
          "
        >
          <button
            :class="bem('row-btn')"
            @click="
              emits(
                'active-swatch-index-changed',
                standardHeroDesigns.length + index
              )
            "
          >
            <DynamicImg
              v-if="variant.legacySKU"
              :sku="variant.legacySKU"
              :width="160"
              :height="160"
              bg="transparent"
              :view-angle="2"
            />
            <span
              v-if="variant.swatchIcon"
              :class="bem('badge')"
            >
              <img :src="variant.swatchIcon" />
            </span>
          </button>
        </SwiperSlide>
      </template>
      <SwiperSlide
        v-if="standardHeroDesigns?.length && showCustomButton"
        :class="bem('row-item', { custom: true, active: props.customButtonActive })"
      >
        <button
          :class="bem('row-btn')"
          @click="emits('toggled-customiser-mode')"
        >
          <span :class="bem('custom-icon-wrapper')">
            <CustomIcon id="image-swatch-select-carousel" :class="bem('custom-icon')"/>
          </span>

          <span :class="bem('custom-icon-text')">Custom</span>
        </button>
      </SwiperSlide>
    </Swiper>

    <div v-if="showDescription" :class="bem('description')">
      <ProductDescription
        :customised-product="customisedProduct"
      />

      <VisionLink
        v-if="!customisedProduct.isLimitedEdition && customisedProduct.lensOptions?.length"
        is-button
        line-link
        theme="gradient"
        :mono-font="true"
        :class="bem('info-button')"
        @click="$emit('show-lens-guide')"
      >
        {{ $tr('Lens Guide') }}
      </VisionLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'

const bem = useBEM('ImageSwatchSelectCarousel')

const emits = defineEmits([
  'active-swatch-index-changed',
  'toggled-customiser-mode',
  'show-lens-guide'
])

const pageStore = usePageStore()

const swiperInstance = ref(null)

interface ImageSwatchSelectCarouselProps {
  variants: Array<any> | never[]
  activeSwatchIndex: number
  customButtonActive?: boolean
  limitedEditionCount: number
  isLimitedEdition: boolean
  customisedProduct: any
  showCustomButton?: boolean
  showDescription?: boolean
}

const props = withDefaults(defineProps<ImageSwatchSelectCarouselProps>(), {
  variants: () => [],
  activeSwatchIndex: 0,
  customButtonActive: false,
  limitedEditionCount: 0,
  isLimitedEdition: false,
  customiserOpen: false,
  customisedProduct: () => ({}),
  showCustomButton: true,
  showDescription: true,
})

const standardHeroDesigns = computed(() => {
  return props.variants
    .filter((variant) => !variant.isLimitedEdition)
    .slice(0, 6)
})

const limitedEditionDesigns = computed(() => {
  return pageStore.showAllLimitedEditionSwatches
    ? props.variants.filter((variant) => variant.isLimitedEdition)
    : props.variants.filter((variant) => variant.isLimitedEdition).slice(0, 6)
})

function setSwiperInstance(instance: any) {
  swiperInstance.value = instance

  /**
   * Bring the active swatch into view if the active index is more 2.
   *
   * This makes sure the active swatch is always visible in the carousel.
   */
  if (props.activeSwatchIndex >= 2) {
    swiperInstance.value.slideTo(props.activeSwatchIndex)
  }
}
</script>

<style lang="postcss">
.ImageSwatchSelectCarousel {
  @apply flex flex-col gap-y-[10px] gap-2;

  margin-left: calc(var(--outer-gutter) * -1);
  margin-right: calc(var(--outer-gutter) * -1);

  container: ImageSwatchSelectCarousel / inline-size;

  .swiper {
    padding-right: var(--outer-gutter);
  }
}

.ImageSwatchSelectCarousel__description {
  @apply mt-3
    text-[16px] text-black
    flex justify-between items-center gap-x-1;

  @screen xsMax {
    @apply flex-col gap-y-1 items-start;
  }

  padding-left: var(--outer-gutter);
  padding-right: var(--outer-gutter);
}

.ImageSwatchSelectCarousel__row {
  @apply flex -mb-6 pb-6 gap-2 w-full;

  padding-left: var(--outer-gutter);
}

.ImageSwatchSelectCarousel__row-item {
  @apply aspect-square bg-grey-200 rounded-[0.25rem] w-[22.22%] mr-2;

  /**
  
    These queries control the size of the elements in the carousel
    before Swiper has initialised. This avoids content resizing page jumps.
  
  */
  @container ImageSwatchSelectCarousel (min-width: 440px) {
    @apply w-[18.18%];
  }

  @container ImageSwatchSelectCarousel (min-width: 580px) {
    @apply w-[15.38%];
  }

  @container ImageSwatchSelectCarousel (min-width: 580px) {
    @apply w-[13.33%];
  }

  &:last-child {
    margin-right: var(--outer-gutter);
  }

  .is-dark-chrome-theme & {
    @apply bg-off-black;
  }
}

.ImageSwatchSelectCarousel__row-item--active {
  .ImageSwatchSelectCarousel__row-btn,
  .ImageSwatchSelectCarousel__row-btn:hover {
    @apply border-black bg-white;

    .is-dark-chrome-theme & {
      @apply border-white;
    }
  }
}

.ImageSwatchSelectCarousel__row-item--custom {
  grid-column: 7;
}

.ImageSwatchSelectCarousel__row-item--show-more {
  @apply text-xxs text-grey-800 font-fragment-mono;
}

.ImageSwatchSelectCarousel__row-btn {
  @apply relative w-full aspect-square
    flex items-center justify-center flex-col gap-y-1
    border border-transparent rounded-[0.25rem];

  .is-dark-chrome-theme & {
    @apply border-grey-700 hover:border-white;
  }

  &:hover {
    .ImageSwatchSelectCarousel__badge {
      @apply border-grey;

      .is-dark-chrome-theme & {
        @apply border-white;
      }
    }
  }
}

.ImageSwatchSelectCarousel__custom-icon-wrapper {
  @apply w-4/12;

  .ImageSwatchSelectCarousel__custom-icon {
    @apply fill-black;
  }

  .is-dark-chrome-theme & {
    .ImageSwatchSelectCarousel__custom-icon {
      @apply fill-white;
    }
  }
}

.ImageSwatchSelectCarousel__custom-icon-text {
  @apply font-fragment-mono text-xxs;
}

.ImageSwatchSelectCarousel__badge {
  @apply w-5/12 min-w-6
    absolute bottom-0 translate-y-1/2
    overflow-hidden
    rounded-[0.625rem] border border-grey-200;
}
</style>
